import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";

import { useAuth } from "pages/context/AuthContext";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogObjetivoV2PatamarClientes from "pages/comercial/objetivosV2/DialogObjetivoV2PatamarClientes";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Draggable from "react-draggable";

function PaperComponent(props) {
  return (
    <Draggable
      handle="#dialog-objetivo-v2-detalhe-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function toISODate(value) {
  if (!value) return "";

  return String(value).slice(0, 10);
}

function fmtNumero(value, casas = 3) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function casasPorMetrica(metrica, modo = "") {
  const m = String(metrica || "")
    .trim()
    .toUpperCase();

  const modoNorm = String(modo || "")
    .trim()
    .toUpperCase();

  if (m === "CLIENTES") {
    return modoNorm === "MEDIA" ? 3 : 0;
  }

  if (m === "QTD") return 0;
  if (m === "EUROS") return 2;
  if (m === "LITROS") return 3;

  return 2;
}

function fmtPercentagem(value, casas = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return `${n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;
}

export default function DialogObjetivoV2Detalhe({ open, onClose, objetivo }) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const [openClientes, setOpenClientes] = useState(false);
  const [patamarSelecionado, setPatamarSelecionado] = useState(null);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      const id = Number(objetivo?.id || 0);

      if (!open || id <= 0) {
        setDetalhe(null);
        return;
      }

      setLoading(true);
      setDetalhe(null);

      try {
        const metrica = String(objetivo?.metrica || "")
          .trim()
          .toUpperCase();

        if (metrica !== "CLIENTES") {
          const [payload, err] = await handleApi(
            api.post("Comercial/SelObjetivoV2ResultadosQuantitativos", {
              id,
              idutilizador: userData?.id || 0,
            })
          );

          if (!ativo) {
            return;
          }

          if (err) {
            showAlert(err, "error");
            return;
          }

          setDetalhe(payload?.data ?? payload ?? null);

          return;
        }

        const [payload, err] = await handleApi(
          api.post("Comercial/SelObjetivoV2ResultadosClientes", {
            id,
            idutilizador: userData?.id || 0,
          })
        );

        if (!ativo) {
          return;
        }

        if (err) {
          showAlert(err, "error");
          return;
        }

        setDetalhe(payload?.data ?? payload ?? null);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [
    open,
    objetivo?.id,
    objetivo?.metrica,
    objetivo?.resultado,
    objetivo?.valor_objetivo,
    objetivo?.atingimento_percentagem,
    objetivo?.contrapartida_percentagem,
    objetivo?.patamares_modo,
    objetivo?.total_patamares,
    showAlert,
  ]);

  const metricaAtual = String(detalhe?.objetivo?.metrica || "")
    .trim()
    .toUpperCase();

  const isClientes = metricaAtual === "CLIENTES";

  const columnsPatamares = useMemo(() => {
    const base = [
      {
        Header: "Ordem",
        accessor: "ordem",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        Header: "Patamar",
        accessor: "nome",
        width: 260,
      },
    ];

    if (isClientes) {
      return [
        ...base,
        {
          Header: "Clientes",
          accessor: "clientes",
          width: 110,
          align: "right",
          headerAlign: "right",
        },
      ];
    }

    return [
      ...base,
      {
        Header: "Resultado",
        accessor: "resultado",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, casasPorMetrica(metricaAtual)),
      },
      {
        Header: "Objetivo",
        accessor: "objetivo",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, casasPorMetrica(metricaAtual)),
      },
      {
        Header: "Atingimento",
        accessor: "atingimento_percentagem",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtPercentagem(value, 2),
      },
    ];
  }, [isClientes, metricaAtual]);

  const rowActionsPatamares = useMemo(
    () => [
      {
        id: "clientes",
        tooltip: "Ver clientes",
        icon: <InfoOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: (row) => {
          setPatamarSelecionado(row);
          setOpenClientes(true);
        },
      },
    ],
    []
  );

  const resultado = detalhe?.resultado;

  const modoObjetivo = String(detalhe?.objetivo?.patamares_modo || "")
    .trim()
    .toUpperCase();

  const descricaoModo = !isClientes
    ? `Resultado em ${metricaAtual}`
    : modoObjetivo === "MEDIA"
    ? "Média de submarcas por cliente"
    : modoObjetivo === "TODAS"
    ? "Clientes que atingiram todos os patamares"
    : "Clientes que atingiram pelo menos um patamar";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperComponent={PaperComponent}>
      <DialogTitle id="dialog-objetivo-v2-detalhe-title" sx={{ cursor: "move" }}>
        Detalhe do objetivo
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <MDBox py={4} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </MDBox>
        ) : detalhe ? (
          <>
            <MDBox mb={2}>
              <MDTypography variant="button" color="text" display="block">
                {objetivo?.objetivo_nome || ""}
                {" · "}
                {toISODate(objetivo?.dataini)}
                {" a "}
                {toISODate(objetivo?.datafim)}
              </MDTypography>

              <MDTypography variant="button" color="text" display="block" mt={0.5}>
                Cálculo: {descricaoModo}
              </MDTypography>
            </MDBox>

            <MDBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              gap={2}
              mb={2}
            >
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Resultado
                  </MDTypography>

                  <MDTypography variant="h5">
                    {fmtNumero(
                      resultado?.valor,
                      casasPorMetrica(detalhe?.objetivo?.metrica, detalhe?.objetivo?.patamares_modo)
                    )}
                  </MDTypography>
                </MDBox>
              </Card>

              <Card>
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Objetivo
                  </MDTypography>

                  <MDTypography variant="h5">
                    {fmtNumero(
                      resultado?.valor_objetivo,
                      casasPorMetrica(detalhe?.objetivo?.metrica, detalhe?.objetivo?.patamares_modo)
                    )}
                  </MDTypography>
                </MDBox>
              </Card>

              <Card>
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Atingimento
                  </MDTypography>

                  <MDTypography variant="h5">
                    {fmtPercentagem(resultado?.atingimento_percentagem, 2)}
                  </MDTypography>
                </MDBox>
              </Card>

              <Card>
                <MDBox p={2}>
                  <MDTypography variant="caption" color="text">
                    Contrapartida
                  </MDTypography>

                  <MDTypography variant="h5">
                    {resultado?.contrapartida_percentagem === null ||
                    resultado?.contrapartida_percentagem === undefined
                      ? ""
                      : fmtPercentagem(resultado.contrapartida_percentagem, 2)}
                  </MDTypography>
                </MDBox>
              </Card>
            </MDBox>

            {modoObjetivo === "MEDIA" && (
              <MDBox
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(3, 1fr)",
                }}
                gap={2}
                mb={2}
              >
                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text">
                      Clientes considerados
                    </MDTypography>

                    <MDTypography variant="h6">
                      {fmtNumero(resultado?.clientes_com_algum_patamar, 0)}
                    </MDTypography>
                  </MDBox>
                </Card>

                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text">
                      Soma de submarcas atingidas pelos clientes
                    </MDTypography>

                    <MDTypography variant="h6">
                      {fmtNumero(resultado?.total_atingimentos_patamares, 0)}
                    </MDTypography>
                  </MDBox>
                </Card>

                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text">
                      Cálculo da média
                    </MDTypography>

                    <MDTypography variant="h6">
                      {fmtNumero(resultado?.total_atingimentos_patamares, 0)}
                      {" / "}
                      {fmtNumero(resultado?.clientes_com_algum_patamar, 0)}
                      {" = "}
                      {fmtNumero(resultado?.valor, 3)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            )}

            <ProjectTable
              tableKey={`objetivos-v2-patamares-${objetivo?.id || "x"}`}
              version="v1"
              columns={columnsPatamares}
              data={detalhe?.patamares || []}
              rowActions={isClientes ? rowActionsPatamares : undefined}
              actionsPosition={isClientes ? "left" : undefined}
              emptyMessage="Sem patamares para apresentar."
              minWidth={760}
              maxHeight="52vh"
              showKeepFilters
            />
          </>
        ) : null}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>

      <DialogObjetivoV2PatamarClientes
        open={openClientes}
        onClose={() => {
          setOpenClientes(false);
          setPatamarSelecionado(null);
        }}
        idObjetivo={objetivo?.id}
        patamar={patamarSelecionado}
      />
    </Dialog>
  );
}
